/**
 * CapaDetalle — detalle de una capa INFORMATIVA: lista sus features con CRUD
 * por-feature (renombrar + borrar individual). NO edita geometría (Fase 5).
 *
 * Lee los features vía useCapas (incluye el id del capa_feature). Mutaciones
 * vía BFF: PATCH/DELETE /v1/capas/features/:featureId (capa_feature_update/delete).
 */
import { useState } from 'react';
import { bffFetch, invalidateCache, useCapas } from '@aosenuma/lib-supabase';
import { showToast } from '@aosenuma/ui';

interface FeatureRow {
  id: string;
  nombre: string;
  geometryType: string;
  propsResumen: string;
}

interface DeleteResult { deleted: boolean; remaining: number }

export function CapaDetalle({ capaId }: { capaId: string }) {
  const { data: capas, loading } = useCapas();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState('');

  const capa = capas.find((c) => c.id === capaId);
  const anyBusy = busyId !== null;

  function refresh() {
    // invalidateCache refetcha a TODOS los suscriptores de estas keys (este
    // useCapas, el registro y los mapas del dashboard/captura) vía el cache bus
    // de la misma pestaña — un solo fetch por recurso, sin refetch() redundante.
    invalidateCache(['capas_informativas', 'capas_registry']);
  }

  const features: FeatureRow[] = (capa?.data.features ?? [])
    .filter((f) => f.id != null)
    .map((f) => {
      const props = (f.properties ?? {}) as Record<string, unknown>;
      const otras = Object.entries(props)
        .filter(([k, v]) => k !== 'feature_nombre' && v != null && v !== '')
        .slice(0, 4)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(' · ');
      return {
        id: String(f.id),
        nombre: typeof props.feature_nombre === 'string' && props.feature_nombre.trim() ? props.feature_nombre.trim() : '(sin nombre)',
        geometryType: f.geometry?.type ?? '—',
        propsResumen: otras,
      };
    });

  async function saveNombre(featureId: string) {
    const n = editNombre.trim();
    if (!n) { showToast('El nombre no puede quedar vacío.', { variant: 'error' }); return; }
    setBusyId(featureId);
    try {
      await bffFetch(`/v1/capas/features/${featureId}`, { method: 'PATCH', body: { nombre: n } });
      refresh();
      showToast('Feature renombrado.', { variant: 'success' });
      setEditId(null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al renombrar.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  async function borrar(featureId: string) {
    if (!confirm('¿Borrar este elemento de la capa? No se puede deshacer.')) return;
    setBusyId(featureId);
    try {
      const res = await bffFetch<DeleteResult>(`/v1/capas/features/${featureId}`, { method: 'DELETE' });
      refresh();
      if (res.remaining === 0) {
        showToast('Elemento borrado. La capa quedó vacía — puedes borrarla desde el registro.', { variant: 'info', ms: 6000 });
      } else {
        showToast(`Elemento borrado (${res.remaining} restante(s)).`, { variant: 'success' });
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al borrar.', { variant: 'error' });
    } finally {
      setBusyId(null);
    }
  }

  if (loading && !capa) return <p className="panel-sub" style={{ padding: '8px 12px' }}>Cargando elementos…</p>;
  // !capa aquí NO es "no existe": CapaDetalle se monta desde una fila del registro
  // (que sí lista capas vacías). v_capas_geojson es INNER JOIN, así que una capa
  // con 0 features no aparece en useCapas → capa undefined = "capa vacía".
  if (!capa || features.length === 0) return <p className="panel-sub" style={{ padding: '8px 12px' }}>Esta capa no tiene elementos.</p>;

  return (
    <table className="data-table" style={{ width: '100%', fontSize: 12, background: 'rgba(74,156,165,0.05)' }}>
      <thead>
        <tr><th>Elemento</th><th>Geometría</th><th>Atributos</th><th>Acciones</th></tr>
      </thead>
      <tbody>
        {features.map((f) => {
          const busy = busyId === f.id;
          return (
            <tr key={f.id} style={{ opacity: busy ? 0.5 : 1 }}>
              <td>
                {editId === f.id ? (
                  <input value={editNombre} maxLength={200} onChange={(e) => setEditNombre(e.target.value)} style={{ width: 180 }} autoFocus />
                ) : (
                  f.nombre
                )}
              </td>
              <td>{f.geometryType}</td>
              <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {f.propsResumen || <em style={{ opacity: 0.6 }}>—</em>}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {editId === f.id ? (
                  <>
                    <button type="button" onClick={() => void saveNombre(f.id)} disabled={anyBusy} style={{ marginRight: 6 }}>Guardar</button>
                    <button type="button" onClick={() => setEditId(null)} disabled={anyBusy}>Cancelar</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => { setEditId(f.id); setEditNombre(f.nombre === '(sin nombre)' ? '' : f.nombre); }} disabled={anyBusy} style={{ marginRight: 6 }}>Renombrar</button>
                    <button type="button" onClick={() => void borrar(f.id)} disabled={anyBusy} style={{ color: 'var(--error, #b56556)' }}>Borrar</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
